import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import {
  Pause,
  Play,
  Trash2,
  Download,
  Smartphone,
  Users,
  AlertTriangle,
  RefreshCw,
  Copy,
  Check,
} from 'lucide-react';
import { useDevices } from '@/hooks/useDevices';
import { useRealtimeDevices } from '@/hooks/useRealtimeDevices';
import { useRealtimeLogs } from '@/hooks/useRealtimeLogs';
import { useDashboardMetrics } from '@/hooks/useDashboard';
import { useExportLogs } from '@/hooks/useLogs';
import { MetricCard } from '@/components/common/MetricCard';
import { MetricCardSkeleton } from '@/components/common/Skeletons';
import { Skeleton } from '@/components/ui/skeleton';
import type { LogLevel } from '@/types/log.types';
import { formatDateTime } from '@/utils/formatters';
import { GradeNumeros, ItemLista, ListaMobile, Selo } from '@/components/common/ListaMobile';
import { IconeAparelho } from '@/pages/Devices/AparelhoMobile';
import { nomePlataforma, rotuloVersao } from '@/utils/aparelho';

// Cor do nível via tokens (--log-*), adaptam ao tema e leem bem no terminal escuro
const LEVEL_TEXT: Record<string, string> = {
  DEBUG: 'text-log-debug',
  INFO: 'text-log-info',
  WARNING: 'text-log-warning',
  ERROR: 'text-log-error',
  FATAL: 'text-log-fatal',
};

// Chip de filtro ativo color-coded por nível
const LEVEL_CHIP_ACTIVE: Record<string, string> = {
  all: 'bg-primary/15 text-primary',
  DEBUG: 'bg-log-debug/15 text-log-debug',
  INFO: 'bg-log-info/15 text-log-info',
  WARNING: 'bg-log-warning/15 text-log-warning',
  ERROR: 'bg-log-error/15 text-log-error',
  FATAL: 'bg-log-fatal/15 text-log-fatal',
};

export function Logs() {
  useRealtimeDevices();

  const [searchParams] = useSearchParams();
  const deviceIdFromUrl = searchParams.get('device');
  const logContainerRef = useRef<HTMLDivElement>(null);
  // No celular os logs ficam abaixo da lista: ao escolher um aparelho a tela rola até eles.
  const secaoLogsRef = useRef<HTMLDivElement>(null);

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(deviceIdFromUrl);
  const [selectedLevel, setSelectedLevel] = useState<LogLevel | 'all'>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  const { data: devicesData, isLoading: devicesLoading } = useDevices();
  const { data: metricsData, isLoading: metricsLoading } = useDashboardMetrics();
  const { logs, isConnected, clearLogs } = useRealtimeLogs(500);
  const { mutate: exportLogs, isPending: isExporting } = useExportLogs();

  const onlineDevices = (devicesData?.devices || []).filter((d) => d.status === 'online');
  const totalDevices = devicesData?.devices?.length ?? 0;
  const selectedDevice = onlineDevices.find((d) => d.id === selectedDeviceId);

  useEffect(() => {
    if (deviceIdFromUrl && !selectedDeviceId) {
      setSelectedDeviceId(deviceIdFromUrl);
    }
  }, [deviceIdFromUrl, selectedDeviceId]);

  const filteredLogs = useMemo(() => {
    const fingerprint = selectedDevice?.fingerprint?.toLowerCase();

    return logs.filter((log) => {
      if (selectedLevel !== 'all' && log.level !== selectedLevel) return false;
      if (!selectedDevice) return true;
      // O log em tempo real traz o fingerprint do aparelho em `deviceId`.
      // Device sem fingerprint (registro antigo) não filtra — mostra tudo.
      if (!fingerprint) return true;
      return log.deviceId?.toLowerCase() === fingerprint;
    });
  }, [logs, selectedLevel, selectedDevice]);

  useEffect(() => {
    if (!autoScroll || isPaused) return;
    const el = logContainerRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(id);
  }, [filteredLogs, autoScroll, isPaused]);

  const handleExportLogs = () => {
    exportLogs({
      deviceId: selectedDeviceId || undefined,
      level: selectedLevel !== 'all' ? selectedLevel : undefined,
    });
  };

  const handleClearLogs = () => {
    if (confirm('Tem certeza que deseja limpar o console?')) {
      clearLogs();
    }
  };

  const handleCopyAllLogs = async () => {
    if (filteredLogs.length === 0) {
      alert('Não há logs para copiar');
      return;
    }

    const logsText = filteredLogs
      .map((log) => `${log.timestamp} [${log.level}] [${log.component}] ${log.message}`)
      .join('\n');

    const header =
      `=== LOGS DO SISTEMA ===\n` +
      `Dispositivo: ${selectedDevice?.userName || 'N/A'} (${selectedDevice?.model || 'N/A'})\n` +
      `Filtro: ${selectedLevel !== 'all' ? selectedLevel : 'Todos os níveis'}\n` +
      `Total de logs: ${filteredLogs.length}\n` +
      `Data de exportação: ${formatDateTime(new Date())}\n` +
      `\n${'-'.repeat(80)}\n\n`;

    const fullText = header + logsText;

    try {
      await navigator.clipboard.writeText(fullText);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (err) {
      console.error('Failed to copy logs:', err);
      alert('Erro ao copiar logs. Tente novamente.');
    }
  };

  return (
    <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Logs do Sistema' }]}>
      <div className="flex flex-col gap-4 p-3 sm:p-4 lg:gap-6 lg:p-6">
        {/* Cabeçalho */}
        <div className="flex flex-col gap-1">
          <h1 className="max-md:hidden text-2xl font-semibold tracking-tight text-text-title sm:text-3xl">
            Logs do Sistema
          </h1>
          <p className="text-sm text-text-secondary">
            Acompanhe os logs dos dispositivos em tempo real.
          </p>
        </div>

        {/* Métricas */}
        {devicesLoading || metricsLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <MetricCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {/* No celular os números ficam num bloco compacto, como em Erros e Dispositivos. */}
            <GradeNumeros
              className="lg:hidden"
              itens={[
                {
                  rotulo: 'online agora',
                  valor: onlineDevices.length,
                  cor: 'text-online',
                  extra: (
                    <span className="ml-1 text-xs font-medium text-text-secondary">
                      de {totalDevices}
                    </span>
                  ),
                },
                { rotulo: 'usuários ativos', valor: metricsData?.activeUsers ?? 0 },
                {
                  rotulo: 'erros críticos',
                  valor: metricsData?.criticalErrors ?? 0,
                  cor: (metricsData?.criticalErrors ?? 0) > 0 ? 'text-error' : undefined,
                },
              ]}
            />
            <div className="hidden grid-cols-3 gap-4 lg:grid lg:gap-6">
              <MetricCard
                title="Dispositivos Online"
                value={onlineDevices.length}
                subtitle={`de ${totalDevices} no total`}
                icon={Smartphone}
                accent="tertiary"
                progress={{ value: onlineDevices.length, max: totalDevices || 1 }}
              />
              <MetricCard
                title="Usuários Ativos"
                value={metricsData?.activeUsers ?? 0}
                subtitle="com login válido"
                icon={Users}
                accent="info"
              />
              <MetricCard
                title="Erros Críticos"
                value={metricsData?.criticalErrors ?? 0}
                subtitle="últimos 7 dias"
                icon={AlertTriangle}
                accent="error"
                valueColor="text-error"
              />
            </div>
          </>
        )}

        {/* Dispositivos online no celular: tocar seleciona e abre os logs abaixo */}
        <ListaMobile
          className="lg:hidden"
          titulo={`Dispositivos online (${onlineDevices.length})`}
          resumo={
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                isConnected ? 'text-online' : 'text-text-hint'
              }`}
            >
              <span className={`size-2 rounded-full ${isConnected ? 'bg-online' : 'bg-offline'}`} />
              {isConnected ? 'Tempo real' : 'Desconectado'}
            </span>
          }
        >
          {devicesLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <ItemLista
                key={i}
                inicio={<Skeleton className="size-10 rounded-xl" />}
                titulo={<Skeleton className="h-4 w-28" />}
                apoio={<Skeleton className="h-3 w-40" />}
              />
            ))
          ) : onlineDevices.length > 0 ? (
            onlineDevices.map((device) => {
              const selecionado = selectedDeviceId === device.id;
              return (
                <ItemLista
                  key={device.id}
                  className={selecionado ? 'bg-primary/5' : undefined}
                  inicio={<IconeAparelho online />}
                  titulo={device.userName}
                  apoio={[
                    device.model,
                    nomePlataforma(device.platform),
                    rotuloVersao(device.appVersion),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  fim={selecionado && <Selo tom="info">Vendo logs</Selo>}
                  onClick={() => {
                    setSelectedDeviceId(device.id);
                    requestAnimationFrame(() =>
                      secaoLogsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    );
                  }}
                />
              );
            })
          ) : (
            <li className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
              <Smartphone size={32} className="text-text-hint" />
              <p className="text-sm font-medium text-text-title">Nenhum dispositivo online</p>
              <p className="text-xs text-text-secondary">Aguardando dispositivos se conectarem</p>
            </li>
          )}
        </ListaMobile>

        {/* Online Devices List (desktop) */}
        <div className="hidden rounded-xl border border-border bg-card p-4 shadow-card lg:block lg:p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-title">
              Dispositivos Online ({onlineDevices.length})
            </h3>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-online opacity-75"></span>
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-online"></span>
                  </span>
                  <span className="text-sm font-medium text-online">Tempo Real Ativo</span>
                </>
              ) : (
                <>
                  <span className="h-2.5 w-2.5 rounded-full bg-offline"></span>
                  <span className="text-sm font-medium text-text-hint">Desconectado</span>
                </>
              )}
            </div>
          </div>
          {devicesLoading ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-1.5 rounded-xl border border-border bg-card p-4"
                >
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-24" />
                </div>
              ))}
            </div>
          ) : onlineDevices.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {onlineDevices.map((device) => (
                <button
                  key={device.id}
                  onClick={() => setSelectedDeviceId(device.id)}
                  aria-pressed={selectedDeviceId === device.id}
                  className={`flex flex-col gap-1.5 rounded-lg border p-4 text-left transition-all hover:shadow-card-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 active:scale-[0.99] ${
                    selectedDeviceId === device.id
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border bg-card hover:border-border-strong'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="relative flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-online opacity-75"></span>
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-online"></span>
                    </div>
                    <p className="truncate font-semibold text-text-title">{device.userName}</p>
                  </div>
                  <p className="truncate text-sm text-text-secondary">{device.model}</p>
                  <p className="text-xs text-text-secondary">
                    {device.platform === 'ios' ? 'iOS' : 'Android'} • v{device.appVersion}
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <Smartphone size={40} className="text-text-hint" />
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium text-text-title">Nenhum dispositivo online</p>
                <p className="text-sm text-text-secondary">Aguardando dispositivos se conectarem</p>
              </div>
            </div>
          )}
        </div>

        {/* Show logs section */}
        <div ref={secaoLogsRef} className="-mb-4 scroll-mt-16 lg:hidden" aria-hidden="true" />
        {!selectedDevice ? (
          <div className="flex w-full items-center justify-center rounded-xl border border-dashed border-border bg-card p-12 shadow-card">
            <div className="flex flex-col items-center gap-3 text-center">
              <span className="flex size-12 items-center justify-center rounded-full bg-muted text-text-hint">
                <Smartphone size={24} />
              </span>
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium text-text-title">Selecione um dispositivo</p>
                <p className="text-sm text-text-secondary">
                  Escolha um dispositivo online para visualizar os logs em tempo real.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Selected Device Info */}
            <div className="rounded-xl border border-border bg-card p-4 shadow-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-online opacity-75"></span>
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-online"></span>
                  </div>
                  <div>
                    <p className="font-semibold text-text-title">{selectedDevice.userName}</p>
                    <p className="text-sm text-text-secondary">
                      {selectedDevice.model} •{' '}
                      {selectedDevice.platform === 'ios' ? 'iOS' : 'Android'}{' '}
                      {selectedDevice.osVersion}
                    </p>
                  </div>
                </div>
                <div className="text-right text-sm">
                  <p className="text-text-secondary">
                    ID:{' '}
                    <span className="font-mono text-text-title">
                      {String(selectedDevice.id).slice(0, 13)}...
                    </span>
                  </p>
                  <p className="text-text-secondary">App: v{selectedDevice.appVersion}</p>
                </div>
              </div>
            </div>

            {/* Log Controls Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 shadow-card">
              <div
                className="flex flex-wrap items-center gap-1.5"
                role="group"
                aria-label="Filtrar por nível"
              >
                {(['all', 'DEBUG', 'INFO', 'WARNING', 'ERROR', 'FATAL'] as const).map((level) => {
                  const active = selectedLevel === level;
                  return (
                    <button
                      key={level}
                      onClick={() => setSelectedLevel(level)}
                      aria-pressed={active}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                        active
                          ? LEVEL_CHIP_ACTIVE[level]
                          : 'bg-muted text-text-secondary hover:bg-bg-elevated hover:text-text-body'
                      }`}
                    >
                      {level === 'all' ? 'Todos' : level}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setIsPaused(!isPaused)}
                  aria-label={isPaused ? 'Retomar stream' : 'Pausar stream'}
                  title={isPaused ? 'Retomar stream' : 'Pausar stream'}
                  className="flex size-9 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  {isPaused ? <Play size={18} /> : <Pause size={18} />}
                </button>
                <button
                  onClick={handleCopyAllLogs}
                  disabled={filteredLogs.length === 0}
                  aria-label="Copiar todos os logs"
                  className={`flex h-9 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50 ${
                    copiedAll
                      ? 'bg-success text-on-tertiary'
                      : 'border border-border-strong bg-card text-text-secondary hover:bg-muted'
                  }`}
                >
                  {copiedAll ? <Check size={16} /> : <Copy size={16} />}
                  <span className="hidden sm:inline">{copiedAll ? 'Copiado!' : 'Copiar'}</span>
                </button>
                <button
                  onClick={handleClearLogs}
                  aria-label="Limpar console"
                  title="Limpar console"
                  className="flex size-9 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <Trash2 size={18} />
                </button>
                <button
                  onClick={handleExportLogs}
                  disabled={isExporting}
                  aria-label="Exportar logs"
                  title="Exportar logs"
                  className="flex size-9 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-muted active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50"
                >
                  {isExporting ? (
                    <RefreshCw size={18} className="animate-spin" />
                  ) : (
                    <Download size={18} />
                  )}
                </button>
              </div>
            </div>

            {/* Log Console */}
            <div className="flex h-[60dvh] min-h-[400px] flex-col rounded-lg bg-zinc-900 p-3 font-mono text-sm text-gray-200 shadow-card lg:h-[600px] lg:p-4">
              <div className="flex flex-shrink-0 items-center justify-between pb-3">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {isConnected && !isPaused ? (
                      <>
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-error opacity-75"></span>
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-error"></span>
                        </span>
                        <span className="text-xs font-bold uppercase tracking-wider text-error">
                          LIVE
                        </span>
                      </>
                    ) : isPaused ? (
                      <>
                        <span className="h-2.5 w-2.5 rounded-full bg-warning"></span>
                        <span className="text-xs font-bold uppercase tracking-wider text-warning">
                          PAUSED
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="h-2.5 w-2.5 rounded-full bg-zinc-600"></span>
                        <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">
                          OFFLINE
                        </span>
                      </>
                    )}
                  </div>
                  <span className="text-xs tabular-nums text-zinc-500">
                    {filteredLogs.length} {selectedLevel !== 'all' ? selectedLevel : ''}{' '}
                    {filteredLogs.length === 1 ? 'log' : 'logs'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-300">Auto-scroll</span>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      checked={autoScroll}
                      onChange={() => setAutoScroll(!autoScroll)}
                      className="peer sr-only"
                      type="checkbox"
                      aria-label="Alternar auto-scroll"
                    />
                    <div
                      className={`peer h-6 w-11 rounded-full transition-colors after:absolute after:left-[2px] after:top-0.5 after:size-5 after:rounded-full after:bg-zinc-100 after:shadow after:transition-transform after:content-[''] peer-checked:after:translate-x-5 peer-focus-visible:ring-2 peer-focus-visible:ring-white/30 ${autoScroll ? 'bg-primary' : 'bg-zinc-700'}`}
                    />
                  </label>
                </div>
              </div>
              <div ref={logContainerRef} className="flex-1 overflow-y-auto pr-2">
                {filteredLogs.length === 0 ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="text-center">
                      <p className="text-zinc-400">
                        {isPaused
                          ? 'Stream pausado'
                          : isConnected
                            ? 'Aguardando logs...'
                            : 'Desconectado do WebSocket'}
                      </p>
                      <p className="mt-2 text-xs text-zinc-500">
                        {isConnected && !isPaused && 'Logs aparecerão aqui em tempo real'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1 text-[13px] leading-relaxed">
                    {filteredLogs.map((log) => {
                      const isApiError =
                        log.metadata?.source === 'API' || log.deviceId === 'api-server';

                      const rowBg =
                        log.level === 'ERROR'
                          ? 'rounded-md bg-error/15 px-2 py-1'
                          : log.level === 'FATAL'
                            ? 'rounded-md bg-log-fatal/15 px-2 py-1'
                            : '';

                      return (
                        <p key={log.id} className={rowBg}>
                          <span className="tabular-nums text-zinc-500">{log.timestamp}</span>{' '}
                          <span className={`font-bold ${LEVEL_TEXT[log.level] ?? 'text-zinc-400'}`}>
                            [{log.level}]
                          </span>{' '}
                          {isApiError && <span className="font-bold text-warning">[API] </span>}
                          <span className="text-zinc-500">[{log.component}]</span>{' '}
                          <span className="text-zinc-200">{log.message}</span>
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
